"use client";

import { useEffect, useState } from "react";

/**
 * Reads the Sanity Studio session token from localStorage on the client.
 *
 * This is the SAME token sanity/tools/useAdminFetch.ts forwards to the
 * app/api/admin/** routes — the Studio (served from /studio on this same
 * origin) writes it under `__studio_auth_token_<projectId>`. Because the
 * /admin/home editor lives outside the Sanity React context we can't call
 * useClient(); we read the persisted token directly instead and forward it as
 * a Bearer header, so the server still gates every request via
 * verifySanitySession — the identical admin-auth mechanism.
 *
 * Returns `undefined` while reading, then `string | null`.
 */
function readSanityToken(projectId: string | undefined): string | null {
  if (typeof window === "undefined") return null;
  try {
    if (projectId) {
      const specific = window.localStorage.getItem(`__studio_auth_token_${projectId}`);
      if (specific) {
        const parsed = JSON.parse(specific) as { token?: string };
        if (parsed?.token) return parsed.token;
      }
    }
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key || !key.startsWith("__studio_auth_token_")) continue;
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as { token?: string };
      if (parsed?.token) return parsed.token;
    }
  } catch {
    // localStorage blocked / malformed JSON — treat as no token.
  }
  return null;
}

export function useSanityToken(): string | null | undefined {
  const [token, setToken] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    setToken(readSanityToken(process.env.NEXT_PUBLIC_SANITY_PROJECT_ID));
  }, []);
  return token;
}
